// Client-side image compression using the Canvas API.
// Converts images to WebP before upload. Falls back to JPEG if WebP is unsupported.
// Preserves original dimensions unless they exceed maxDimension. Never upscales.
//
// Usage (single pass, exact preset):
//   import { compressImage } from '../field_image_compressor'
//   const compressed = await compressImage(file, { quality: 0.85, maxDimension: 2560 })
//   // compressed is a File with updated name and content type
//
// Usage (byte-targeted ladder -- preferred at all call sites, cards #2433, #3518 review):
//   import { compressImageWithRetry, CompressionError } from '../field_image_compressor'
//   try {
//     const compressed = await compressImageWithRetry(file)
//     compressed.compression // => { rung, width, height, bytes, attempts, targetHit }
//   } catch (error) {
//     if (error instanceof CompressionError) { /* decode failed or input over MAX_INPUT_BYTES */ }
//   }
//
// Ladder semantics (2026-09-22 photo pipeline review, zenith/features/field-app
// README "Photo pipeline review vs rt-client and GoSpotCheck"):
//
// #3518 raised the ladder to 4096 / q0.95 with no byte target, on the premise
// that rt-client uploaded full-resolution photos. Measured against production:
// the median field WebP went 189KB -> 1,411KB (7.5x), p90 2.4MB, and at a
// throttled 50KB/s a median photo was brushing the upload timeout. rt-client
// actually walks a dimension ladder (2560 -> 2048 -> 1600 -> ...) toward a
// byte ceiling; GoSpotCheck lived at 1600px / ~700KB median for years.
//
// So the ladder is now SIZE-targeted, not failure-driven: every rung is
// encoded in order and the walk stops at the first output <= TARGET_BYTES.
// A small photo lands at rung 1 in one attempt; a busy 12MP shelf shot walks
// down until it fits. If NO rung fits, the smallest successful output is
// kept -- size never throws. CompressionError is reserved for total decode
// failure (every rung failed) and the MAX_INPUT_BYTES cap.
//
// Card #3512: each rung hands createImageBitmap a downscale-only resize hint
// so the browser can decode directly to that rung's output size, and falls
// back to decoding via an <img> element (a different browser pipeline) if
// createImageBitmap throws. See _decodeSource below.
//
// Card #3363: HEIC/HEIF. Chrome, Edge and Firefox can't decode HEIC, so an
// iPhone photo saved from Messages/AirDrop/email failed every rung above and
// was skipped. Before the ladder runs, _prepareSource detects HEIC (MIME type,
// .heic/.heif extension, or the ISO-BMFF `ftyp` brand -- Chrome on Windows
// often reports an empty type), tries the browser's own decoder first
// (Safari, which handles HEIC natively, carries on exactly as before), and
// otherwise converts to a high-quality JPEG with the lazily-loaded
// heic_decoder.js module. The JPEG then walks the ladder unchanged, so the
// server still only ever sees WebP/JPEG. Videos (the .mov half of a Live
// Photo) are rejected up front with their own reason. Every failure is a
// CompressionError carrying a `reason` (COMPRESSION_FAILURE) so callers can
// tell the user WHY, rather than string-matching the message.

import { logEvent } from "./field_logger"

const WEBP_SUPPORTED = (() => {
  const canvas = document.createElement("canvas")
  canvas.width = 1
  canvas.height = 1
  return canvas.toDataURL("image/webp").startsWith("data:image/webp")
})()

// Hard input cap -- reject before even attempting to decode. A file this large
// (full-res gallery photo, unconverted HEIC) isn't worth the memory pressure
// of createImageBitmap on a low-end device, and every rung below would fail
// anyway. Raised from 20MB to 30MB (#3494) -- newer phone cameras (48MP+ HEIC)
// were tripping the old cap on legitimate full-res photos before compression
// ever got a chance to run.
export const MAX_INPUT_BYTES = 30 * 1024 * 1024 // 30MB

// Soft byte target for a compressed photo. The ladder stops at the first rung
// whose output fits under this; it is a ceiling to walk toward, not a hard
// limit -- an image that can't get under it at any rung still uploads at its
// smallest rung. ~1MB is roughly half the post-#3518 production median and
// still clears GoSpotCheck's ~700KB median on both resolution and quality.
export const TARGET_BYTES = 1 * 1024 * 1024 // 1MB

// Dimension/quality ladder, walked top-down until output <= TARGET_BYTES.
// Mirrors rt-client's shape (2560 -> 2048 -> 1600 -> 1280 -> 1024 -> 800 -> 640)
// without going as low: 1280 is the floor, still above the "potato" era's
// 1200 bottom rung and comfortably above what a shelf photo needs to be read.
// Top rung 2560 replaces #3518's 4096 -- that number was set on a wrong
// premise about rt-client (see header) and is what produced the 1.4MB median.
export const COMPRESSION_PRESETS = [
  { maxDimension: 2560, quality: 0.85 },
  { maxDimension: 2048, quality: 0.80 },
  { maxDimension: 1600, quality: 0.75 },
  { maxDimension: 1280, quality: 0.70 },
]

// Why a photo couldn't be compressed -- read from CompressionError#reason.
export const COMPRESSION_FAILURE = Object.freeze({
  TOO_LARGE:              "too_large",              // over MAX_INPUT_BYTES
  DECODE_FAILED:          "decode_failed",          // every ladder rung failed
  HEIC_CONVERSION_FAILED: "heic_conversion_failed", // HEIC the browser can't read and we couldn't convert
  VIDEO:                  "video",                  // a video (e.g. a Live Photo's .mov), not a photo
})

const TOO_LARGE_MESSAGE = "Photo too large — try again"
const HEIC_FAILED_MESSAGE = "This HEIC photo couldn't be converted. Save it as JPEG and try again."
const VIDEO_MESSAGE = "Videos can't be added. Use the photo instead."

// `reason` is one of COMPRESSION_FAILURE. `detectedType` is how the file was
// classified ("heic:type", "heic:extension", "heic:magic", "video", or its
// raw MIME type), and `heicConversionAttempted` says whether heic_decoder.js
// was actually invoked -- both are there for logError payloads.
export class CompressionError extends Error {
  constructor(message, { reason = COMPRESSION_FAILURE.DECODE_FAILED, detectedType = null, heicConversionAttempted = false, ...options } = {}) {
    super(message, options)
    this.name = "CompressionError"
    this.reason = reason
    this.detectedType = detectedType
    this.heicConversionAttempted = heicConversionAttempted
  }
}

// ── HEIC decoder location (card #3363) ──────────────────────────────────────
// heic_decoder.js is its own esbuild entry point with a fingerprinted URL, so
// the page has to tell us where it is. Resolution order:
//   1. options.heicDecoderUrl passed to compressImageWithRetry/-Report
//   2. setHeicDecoderUrl(url) -- the survey-result-editor controller calls this
//      from its heicDecoderUrl Stimulus value, which _edit.html.erb renders.
//      That's what covers the desktop "Unlock to edit" path, where the editor
//      is injected by a Turbo Stream into whatever page is already open.
//   3. <meta name="heic-decoder-url"> in the application and field layouts --
//      the shared fallback for field_photos_controller and
//      field_offline_app_controller.
let configuredHeicDecoderUrl = null

export function setHeicDecoderUrl(url) {
  if (url) configuredHeicDecoderUrl = url
}

function _resolveHeicDecoderUrl(explicitUrl) {
  return explicitUrl ||
    configuredHeicDecoderUrl ||
    document.querySelector('meta[name="heic-decoder-url"]')?.content ||
    null
}

// Generous: the decoder is ~3MB and a first HEIC on a slow link has to fetch
// it. Both exist so a stalled load or a wedged worker fails with the HEIC
// message instead of leaving the upload spinner going forever.
const HEIC_DECODER_LOAD_TIMEOUT_MS = 60_000
const HEIC_CONVERT_TIMEOUT_MS      = 60_000
const HEIC_JPEG_QUALITY            = 0.92

// Walks the ladder against `file` and returns the chosen output as a File.
// The File carries a `compression` property with the report from
// compressImageWithReport below, so callers that only want the File keep
// their existing contract and callers that want the rung can read it.
//
// Throws CompressionError if the file exceeds MAX_INPUT_BYTES, or if every
// rung fails to decode/encode -- callers must handle this and tell the tasker
// rather than silently proceeding with the original file (#2433). Output SIZE
// never throws.
export async function compressImageWithRetry(file, presets = COMPRESSION_PRESETS, options = {}) {
  const report = await compressImageWithReport(file, presets, options)
  report.file.compression = _reportSummary(report)
  return report.file
}

// Same walk as compressImageWithRetry, returning the full report:
//   { file, rung, width, height, bytes, attempts, targetHit }
// rung is 1-based (rung 1 = presets[0]); attempts is how many rungs were
// encoded before stopping; targetHit is whether the returned output is under
// targetBytes. Logs one "Photo compressed" camera event per call so the
// ladder's behaviour is verifiable in production app_logs.
export async function compressImageWithReport(originalFile, presets = COMPRESSION_PRESETS, { targetBytes = TARGET_BYTES, heicDecoderUrl = null } = {}) {
  if (isVideoFile(originalFile)) {
    console.warn(`[compress] ${originalFile.name}: is a video (${originalFile.type || "no type"}), rejecting`)
    throw new CompressionError(VIDEO_MESSAGE, { reason: COMPRESSION_FAILURE.VIDEO, detectedType: "video" })
  }

  if (originalFile.size > MAX_INPUT_BYTES) {
    console.warn(`[compress] ${originalFile.name}: ${_kb(originalFile.size)}KB exceeds ${_kb(MAX_INPUT_BYTES)}KB cap, rejecting`)
    throw new CompressionError(TOO_LARGE_MESSAGE, { reason: COMPRESSION_FAILURE.TOO_LARGE, detectedType: originalFile.type || null })
  }

  const { file, detectedType, heicConversionAttempted } = await _prepareSource(originalFile, { heicDecoderUrl })

  let best = null
  let lastError
  let attempts = 0

  for (let index = 0; index < presets.length; index++) {
    const preset = presets[index]
    attempts++
    try {
      const encoded = await _encode(file, preset)
      if (!best || encoded.file.size < best.file.size) {
        best = { ...encoded, rung: index + 1 }
      }
      if (encoded.file.size <= targetBytes) break
    } catch (error) {
      lastError = error
      console.warn(`[compress] ${file.name}: rung ${index + 1} ${JSON.stringify(preset)} failed`, error)
    }
  }

  if (!best) {
    throw new CompressionError(TOO_LARGE_MESSAGE, {
      cause: lastError,
      reason: COMPRESSION_FAILURE.DECODE_FAILED,
      detectedType,
      heicConversionAttempted
    })
  }

  const report = {
    file:      best.file,
    rung:      best.rung,
    width:     best.width,
    height:    best.height,
    bytes:     best.file.size,
    attempts,
    targetHit: best.file.size <= targetBytes
  }

  console.log(`[compress] ${file.name}: ${_kb(originalFile.size)}KB → ${_kb(report.bytes)}KB at rung ${report.rung} (${report.width}x${report.height}, ${attempts} attempt${attempts === 1 ? "" : "s"}, target ${report.targetHit ? "hit" : "missed"})`)
  logEvent("camera", "Photo compressed", _reportSummary(report)).catch(() => {})

  return report
}

// Single-pass encode at an exact preset. Used directly by callers that want
// one specific size (and by the #3512 decode specs); the ladder above goes
// through _encode to also get the output dimensions.
export async function compressImage(file, { quality = 0.85, maxDimension = 2560 } = {}) {
  const { file: compressed } = await _encode(file, { quality, maxDimension })
  return compressed
}

// ── HEIC detection and conversion (card #3363) ──────────────────────────────

const HEIC_MIME_TYPES = ["image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence"]
const HEIC_EXTENSION  = /\.(heic|heif)$/i
// ISO-BMFF major brands used by HEIC/HEIF stills and sequences.
const HEIC_BRANDS     = ["heic", "heix", "hevc", "hevx", "heim", "heis", "mif1", "msf1"]
// Types the browser decodes everywhere -- no point sniffing their bytes.
const PLAIN_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"]
const VIDEO_EXTENSION = /\.(mov|mp4|m4v|3gp|avi|webm|hevc)$/i

// True for a video file -- by MIME type, or by extension when the browser
// gave no useful type. Live Photos shared off an iPhone often arrive as a
// .heic plus a .mov; the .mov gets a clear "use the photo" message.
export function isVideoFile(file) {
  const type = (file.type || "").toLowerCase()
  if (type.startsWith("video/")) return true
  if (type && type !== "application/octet-stream") return false
  return VIDEO_EXTENSION.test(file.name || "")
}

// Classifies `file` as HEIC/HEIF. Returns "heic:type", "heic:extension",
// "heic:magic", or null. Checks, in order: the MIME type; an empty/generic
// type with a .heic/.heif name (Chrome on Windows commonly reports ""); then
// the file's own first bytes -- `ftyp` at offset 4 and a HEIC brand at 8 --
// for anything that isn't already a plain JPEG/PNG/WebP/GIF. Only the first
// 32 bytes are read.
export async function detectHeic(file) {
  const type = (file.type || "").toLowerCase()
  if (HEIC_MIME_TYPES.includes(type)) return "heic:type"

  const genericType = !type || type === "application/octet-stream"
  if (genericType && HEIC_EXTENSION.test(file.name || "")) return "heic:extension"

  if (PLAIN_IMAGE_TYPES.includes(type)) return null
  return (await _hasHeicSignature(file)) ? "heic:magic" : null
}

async function _hasHeicSignature(file) {
  try {
    const bytes = new Uint8Array(await file.slice(0, 32).arrayBuffer())
    if (bytes.length < 12) return false
    const ascii = (from, to) => String.fromCharCode(...bytes.slice(from, to))
    return ascii(4, 8) === "ftyp" && HEIC_BRANDS.includes(ascii(8, 12))
  } catch {
    return false // unreadable handle -- let the ladder report the real failure
  }
}

// Returns the file the ladder should decode, plus how it was classified. For
// anything that isn't HEIC this is the original file untouched. For HEIC the
// browser gets first go (Safari decodes it natively -- no conversion, no
// download); otherwise it's converted to JPEG via heic_decoder.js. Throws a
// HEIC_CONVERSION_FAILED CompressionError if that conversion can't happen
// (decoder URL missing, offline with no cached decoder, corrupt file, timeout).
async function _prepareSource(file, { heicDecoderUrl }) {
  const heicType = await detectHeic(file)
  if (!heicType) return { file, detectedType: file.type || null, heicConversionAttempted: false }

  if (await _browserCanDecode(file)) {
    return { file, detectedType: heicType, heicConversionAttempted: false }
  }

  const startedAt = performance.now()
  let jpegBlob
  try {
    const decoder = await _withTimeout(
      _loadHeicDecoder(_resolveHeicDecoderUrl(heicDecoderUrl)),
      HEIC_DECODER_LOAD_TIMEOUT_MS,
      "HEIC decoder load timed out"
    )
    jpegBlob = await _withTimeout(
      decoder.convertHeicToJpeg(file, { quality: HEIC_JPEG_QUALITY }),
      HEIC_CONVERT_TIMEOUT_MS,
      "HEIC conversion timed out"
    )
  } catch (error) {
    const cause = error instanceof Error ? error : new Error(String(error))
    console.warn(`[compress] ${file.name}: HEIC conversion failed`, cause)
    throw new CompressionError(HEIC_FAILED_MESSAGE, {
      cause,
      reason: COMPRESSION_FAILURE.HEIC_CONVERSION_FAILED,
      detectedType: heicType,
      heicConversionAttempted: true
    })
  }

  const ms = Math.round(performance.now() - startedAt)
  const baseName = (file.name || "photo").replace(/\.[^.]+$/, "")
  const converted = new File([jpegBlob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: file.lastModified || Date.now() })

  console.log(`[compress] ${file.name}: HEIC ${_kb(file.size)}KB → JPEG ${_kb(converted.size)}KB in ${ms}ms`)
  logEvent("upload", "HEIC converted", {
    originalSize:  file.size,
    convertedSize: converted.size,
    ms,
    detectedType:  heicType
  }).catch(() => {})

  return { file: converted, detectedType: heicType, heicConversionAttempted: true }
}

// Cheap native-decode probe: the <img> header parse from _probeImageElement.
// Safari resolves; Chrome/Edge/Firefox reject straight away for HEIC.
async function _browserCanDecode(file) {
  try {
    const probe = await _probeImageElement(file)
    probe.close()
    return true
  } catch {
    return false
  }
}

// One shared import per page. The URL is a runtime value, so esbuild leaves
// this import() alone rather than bundling heic_decoder.js into
// application.js. A failed load is forgotten so the next HEIC retries (e.g.
// the user came back online).
let heicDecoderModule = null

function _loadHeicDecoder(url) {
  if (!url) return Promise.reject(new Error("HEIC decoder URL not configured"))
  if (!heicDecoderModule || heicDecoderModule.url !== url) {
    const promise = import(url).catch(error => {
      if (heicDecoderModule?.promise === promise) heicDecoderModule = null
      throw error
    })
    heicDecoderModule = { url, promise }
  }
  return heicDecoderModule.promise
}

function _withTimeout(promise, ms, message) {
  let timer
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

async function _encode(file, { quality, maxDimension }) {
  const source = await _decodeSource(file, maxDimension)
  const { width, height } = _scaledDimensions(source.width, source.height, maxDimension)

  const canvas = document.createElement("canvas")
  canvas.width  = width
  canvas.height = height
  const ctx = canvas.getContext("2d")
  ctx.drawImage(source.image, 0, 0, width, height)
  source.close()

  const mimeType = WEBP_SUPPORTED ? "image/webp" : "image/jpeg"
  const ext      = WEBP_SUPPORTED ? "webp" : "jpg"
  const blob     = await _canvasToBlob(canvas, mimeType, quality)
  const baseName = file.name.replace(/\.[^.]+$/, "")

  const compressed = new File([blob], `${baseName}.${ext}`, { type: mimeType, lastModified: Date.now() })
  return { file: compressed, width, height }
}

function _reportSummary({ rung, width, height, bytes, attempts, targetHit }) {
  return { rung, width, height, bytes, attempts, targetHit }
}

// Decodes `file` into a drawable source (an ImageBitmap or, on fallback, an
// HTMLImageElement) plus its dimensions. This is the fix for card #3512 --
// "Photo too large" recurring on real gallery uploads well under
// MAX_INPUT_BYTES, traced to createImageBitmap(file) decoding the full
// original on every ladder rung with no resize hint, so a genuine
// decode-time failure (memory pressure) failed every rung identically.
//
// Step 1 -- probe. An <img> pointed at an object URL fires `load` once the
// header is parsed and naturalWidth/naturalHeight are known; the browser
// defers the full pixel decode until paint or .decode(). That gives the
// source dimensions cheaply, which matters because createImageBitmap's
// `resizeWidth` is a RESIZE, not a cap: handing it maxDimension for an
// 800px screenshot would upscale the bitmap to 2560 and _scaledDimensions
// would happily keep it there. With the natural size known, the hint is the
// exact downscale-only output width (never larger than the source), so the
// bitmap comes back at final size on both axes.
//
// Step 2 -- createImageBitmap with that hint, so the browser decodes
// directly to the target instead of first materialising the full-resolution
// bitmap in memory. Only ONE resize dimension is passed, not both -- per
// spec, giving both resizeWidth and resizeHeight stretches the image to fit
// exactly, distorting the aspect ratio, whereas a single dimension lets the
// browser preserve it. resizeQuality: "medium" -- a reasonable middle
// ground given this is a downscale hint feeding a canvas re-encode, not the
// final visual quality (that's `quality` above).
//
// Step 3 -- if createImageBitmap throws (format/memory quirks it can't
// handle), fall back to fully decoding the SAME <img> from step 1 -- a
// different browser decode pipeline that's sometimes more tolerant. Only
// engaged on failure; the happy path's performance is one header parse plus
// one resize-decode.
//
// If the <img> can't even load the file (a format createImageBitmap may
// still understand), the probe is skipped and the hint falls back to
// maxDimension as before #3512's follow-up -- the upscale guard is lost for
// that edge case only.
async function _decodeSource(file, maxDimension) {
  const probe = await _probeImageElement(file).catch(() => null)
  const target = probe ? _scaledDimensions(probe.width, probe.height, maxDimension) : null

  try {
    const bitmap = await createImageBitmap(file, {
      resizeWidth: target ? target.width : maxDimension,
      resizeQuality: "medium",
    })
    probe?.close()
    return { image: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() }
  } catch (bitmapError) {
    console.warn(`[compress] ${file.name}: createImageBitmap failed, falling back to <img> decode`, bitmapError)
    if (probe) return _decodeViaImageElement(probe)
    return _decodeViaImageElement(await _probeImageElement(file))
  }
}

// Creates an <img> over an object URL for `file` and resolves once its
// natural dimensions are known (the `load` event -- header parse, no full
// pixel decode yet). Returns { image, url, width, height, close }; `close`
// revokes the object URL. Rejects if the browser can't load the file at all.
function _probeImageElement(file) {
  const url = URL.createObjectURL(file)
  const img = new Image()

  return new Promise((resolve, reject) => {
    img.onload = () => resolve({
      image:  img,
      url,
      width:  img.naturalWidth,
      height: img.naturalHeight,
      close:  () => URL.revokeObjectURL(url),
    })
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error("Image element failed to load"))
    }
    img.src = url
  })
}

// Fallback decode path for card #3512 -- takes the probed <img> and waits for
// it to fully decode via the modern `.decode()` API (browsers without it have
// already fired `load` and can be drawn as-is). Revokes the object URL on
// failure here, or via the returned `close` once the caller finishes drawing.
async function _decodeViaImageElement(probe) {
  const { image } = probe

  try {
    if (typeof image.decode === "function") await image.decode()
  } catch (error) {
    probe.close()
    throw error
  }

  return probe
}

function _scaledDimensions(w, h, max) {
  if (w <= max && h <= max) return { width: w, height: h }
  const ratio = Math.min(max / w, max / h)
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) }
}

function _canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error("Canvas toBlob failed")),
      type,
      quality
    )
  })
}

function _kb(bytes) {
  return (bytes / 1024).toFixed(1)
}
